import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styles from './table.module.css'

/**
 * `value` is `string | number` because not everything this menu picks has an id. The label filter
 * used array indices as values, and `knownLabels` is recomputed from the task list on every
 * refetch — so a label added or removed anywhere shifted the indices and the filter silently
 * started pointing at a different label than the one on screen.
 */
export interface MenuOption {
  value: string | number
  label: ReactNode
}

const MENU_WIDTH = 150
const VIEWPORT_MARGIN = 8

/**
 * Inline edit for status and priority, and the toolbar's filter dropdowns (ui-spec §5.3).
 *
 * The menu renders into `document.body`, not next to its trigger. Both places that host this
 * component sit inside a scroll container — the toolbar has `overflow-x: auto`, the row list has
 * `overflow-y: auto` — and per the CSS spec, a box that scrolls on one axis stops being `visible`
 * on the other. An absolutely positioned menu inside either one gets **clipped**, which is not a
 * z-index problem and cannot be fixed with one: the box is cutting, not painting over.
 */
export function InlineCellMenu({
  trigger,
  options,
  value,
  onSelect,
  label,
}: {
  trigger: ReactNode
  options: MenuOption[]
  value: string | number
  onSelect: (value: string | number) => void
  label: string
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const anchor = useRef<HTMLDivElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const triggerButton = useRef<HTMLButtonElement>(null)
  const items = useRef<(HTMLButtonElement | null)[]>([])

  /**
   * Closing always hands focus back to the trigger. Without this the caret lands on <body> and the
   * next Tab restarts from the top of the document — the menu would be operable by keyboard right
   * up to the moment you leave it (ui-spec §11).
   */
  const close = (restoreFocus = true): void => {
    setOpen(false)
    if (restoreFocus) triggerButton.current?.focus()
  }

  // Opening puts the caret on the current value, which is where a menu is expected to start.
  const openMenu = (): void => {
    const current = options.findIndex((o) => o.value === value)
    setActiveIndex(current >= 0 ? current : 0)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    items.current[activeIndex]?.focus()
  }, [open, activeIndex])

  // Layout effect, not a plain one: measuring after paint would show the menu at 0,0 for a frame.
  useLayoutEffect(() => {
    if (!open || !anchor.current) return

    const rect = anchor.current.getBoundingClientRect()
    const height = menu.current?.offsetHeight ?? 0

    // Flip above the trigger when there is no room below — a menu opened in the last row would
    // otherwise hang off the bottom of the window.
    const below = rect.bottom + 4
    const fitsBelow = below + height + VIEWPORT_MARGIN <= window.innerHeight
    const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, rect.top - height - 4)

    const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN)
    setPosition({ top, left: Math.max(VIEWPORT_MARGIN, left) })
  }, [open, options.length])

  useEffect(() => {
    if (!open) return

    const onDocumentDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (!anchor.current?.contains(target) && !menu.current?.contains(target)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    }
    // A fixed menu does not travel with the page. Closing beats recomputing on every scroll frame,
    // and matches what the rest of the app does when the ground moves.
    // Focus stays where the user put it: the ground moved, they did not ask to leave the menu.
    //
    // The capture phase is required to hear scrolling in the containers the menu escaped from —
    // scroll does not bubble. It also means this fires when the menu scrolls its OWN long list,
    // which closed the menu the instant anybody tried to reach an option further down.
    const onScroll = (event: Event): void => {
      if (menu.current?.contains(event.target as Node)) return
      close(false)
    }
    const onResize = (): void => close(false)

    document.addEventListener('mousedown', onDocumentDown)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDocumentDown)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  return (
    <div
      ref={anchor}
      className={styles.menuAnchor}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <button
        ref={triggerButton}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => {
          // Down-arrow opens straight into the list, which is what a menu button does everywhere
          // else. Without it the only way in is a click.
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            openMenu()
          }
        }}
      >
        {trigger}
      </button>

      {open &&
        createPortal(
          <div
            ref={menu}
            className={styles.menu}
            role="menu"
            tabIndex={-1}
            aria-label={label}
            // Hidden until measured, rather than visible at the wrong place for one frame.
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              // role="menu" is a promise that arrows work. Without this block the attribute told
              // screen readers to expect a menu and the keyboard delivered a plain list of buttons.
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIndex((i) => (i + 1) % options.length)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIndex((i) => (i - 1 + options.length) % options.length)
              } else if (e.key === 'Home') {
                e.preventDefault()
                setActiveIndex(0)
              } else if (e.key === 'End') {
                e.preventDefault()
                setActiveIndex(options.length - 1)
              }
            }}
          >
            {options.map((option, index) => (
              <button
                key={String(option.value)}
                ref={(el) => {
                  items.current[index] = el
                }}
                type="button"
                role="menuitemradio"
                aria-checked={option.value === value}
                // Roving tabindex: one stop for the whole menu, arrows move within it.
                tabIndex={index === activeIndex ? 0 : -1}
                className={`${styles.menuItem}${option.value === value ? ` ${styles.menuItemActive}` : ''}`}
                onClick={() => {
                  onSelect(option.value)
                  close()
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
